import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, TrendingUp, TrendingDown } from "lucide-react";
import api from "../lib/api";
import { fmtPrice, fmtPct, fmtNum, changeClass } from "../lib/format";
import { Skeleton } from "../components/ui/skeleton";
import { DigestPanel } from "../components/DigestPanel";

const MoverRow = ({ s, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`mover-row-${s.ticker}`}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-left even:bg-white/[0.02] hover:bg-white/5 transition-colors duration-150 group"
  >
    <span className="font-mono text-sm font-medium text-white w-16 group-hover:text-primary transition-colors duration-150">
      {s.ticker}
    </span>
    <span className="flex-1 text-xs text-muted-foreground truncate">{s.name}</span>
    <span className="font-mono text-sm text-gray-300">{fmtPrice(s.quote?.price, s.currency)}</span>
    <span className={`font-mono text-sm w-20 text-right ${changeClass(s.quote?.change_pct)}`}>
      {fmtPct(s.quote?.change_pct)}
    </span>
  </button>
);

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [presets, setPresets] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/market/overview").then((r) => setOverview(r.data));
    api.get("/scanner/presets").then((r) => setPresets(r.data.presets));
  }, []);

  const refreshing = overview?.refresh_status?.state === "running";

  return (
    <div className="space-y-8 fade-up">
      <div>
        <div className="overline-label mb-2">Market Overview</div>
        <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl text-white">Dashboard</h1>
        {refreshing && (
          <p className="text-xs font-mono text-yellow-500 mt-2" data-testid="refresh-banner">
            CACHE REFRESH IN PROGRESS · {overview.refresh_status.done}/{overview.refresh_status.total} tickers
          </p>
        )}
      </div>

      {/* Indices */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="indices-grid">
        {!overview
          ? [0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-sm bg-card" />)
          : overview.indices.map((idx) => (
              <div key={idx.symbol} className="widget p-5" data-testid={`index-card-${idx.symbol.replace("^", "")}`}>
                <div className="overline-label">{idx.name}</div>
                <div className="flex items-end justify-between mt-3">
                  <span className="font-mono text-2xl font-medium text-white">{fmtNum(idx.price)}</span>
                  <span className={`flex items-center gap-1 font-mono text-sm ${changeClass(idx.change_pct)}`}>
                    {idx.change_pct >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    {fmtPct(idx.change_pct)}
                  </span>
                </div>
              </div>
            ))}
      </div>

      {/* Movers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="widget" data-testid="top-gainers-widget">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gain" />
            <span className="overline-label text-gain">Top gainers · today</span>
          </div>
          {!overview ? (
            <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 bg-secondary" />)}</div>
          ) : (
            overview.gainers.map((s) => <MoverRow key={s.ticker} s={s} onClick={() => navigate(`/stock/${s.ticker}`)} />)
          )}
        </div>
        <div className="widget" data-testid="top-losers-widget">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <TrendingDown className="w-4 h-4 text-loss" />
            <span className="overline-label text-loss">Top losers · today</span>
          </div>
          {!overview ? (
            <div className="p-4 space-y-2">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-8 bg-secondary" />)}</div>
          ) : (
            overview.losers.map((s) => <MoverRow key={s.ticker} s={s} onClick={() => navigate(`/stock/${s.ticker}`)} />)
          )}
        </div>
      </div>

      <DigestPanel />

      {/* Preset quick links */}
      <div>
        <div className="overline-label mb-4">Preset screens</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="preset-links-grid">
          {presets.map((p) => (
            <Link
              key={p.id}
              to={`/scanner?preset=${p.id}`}
              data-testid={`preset-link-${p.id}`}
              className="widget p-5 group hover:border-primary/60 transition-colors duration-150 block"
            >
              <div className="font-heading font-bold text-white tracking-tight mb-2 flex items-center justify-between">
                {p.name}
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors duration-150" />
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">{p.description}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
